import React, { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { Sidebar, BottomNav } from './components/Navigation';
import { AuthScreen } from './screens/AuthScreen';
import { LandingPage } from './screens/LandingPage';
import { OnboardingGuide } from './components/OnboardingGuide';
import { OnboardingWizard } from './components/OnboardingWizard';
import { SubscriptionRequiredScreen } from './screens/SubscriptionRequiredScreen';
import { useStore } from './store/useStore';

const ServiceCreationScreen = lazy(() => import('./screens/ServiceCreationScreen').then(m => ({ default: m.ServiceCreationScreen })));
const CalendarScreen = lazy(() => import('./screens/CalendarScreen').then(m => ({ default: m.CalendarScreen })));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen').then(m => ({ default: m.SettingsScreen })));
const CasesScreen = lazy(() => import('./screens/CasesScreen').then(m => ({ default: m.CasesScreen })));
const ServicesManagementScreen = lazy(() => import('./screens/ServicesManagementScreen').then(m => ({ default: m.ServicesManagementScreen })));
const DocumentTemplatesScreen = lazy(() => import('./screens/DocumentTemplatesScreen').then(m => ({ default: m.DocumentTemplatesScreen })));
const ClientsScreen = lazy(() => import('./screens/ClientsScreen').then(m => ({ default: m.ClientsScreen })));
const CaseDetailsScreen = lazy(() => import('./screens/CaseDetailsScreen').then(m => ({ default: m.CaseDetailsScreen })));
const AnalyticsScreen = lazy(() => import('./screens/AnalyticsScreen').then(m => ({ default: m.AnalyticsScreen })));

export type View = 'landing' | 'dashboard' | 'newService' | 'calendar' | 'cases' | 'settings' | 'servicesManagement' | 'documentTemplates' | 'clients' | 'caseDetails' | 'analytics';

const App: React.FC = () => {
  const { 
    currentUser, 
    setCurrentUser, 
    fetchAllData, 
    selectedService, 
    setSelectedService,
    logout,
    initializeAuth,
    isLoading
  } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveView = (): View => {
      const p = location.pathname;
      if (p.includes('new-service')) return 'newService';
      if (p.includes('calendar')) return 'calendar';
      if (p.includes('cases')) return 'cases';
      if (p.includes('settings')) return 'settings';
      if (p.includes('catalog')) return 'servicesManagement';
      if (p.includes('templates')) return 'documentTemplates';
      if (p.includes('clients')) return 'clients';
      if (p.includes('case-details')) return 'caseDetails';
      if (p.includes('analytics')) return 'analytics';
      if (p === '/') return 'landing';
      return 'dashboard';
  };
  
  const view = getActiveView();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    initializeAuth(() => {
        setIsInitializing(false);
    });
  }, [initializeAuth]);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [isDarkMode]);
  
  useEffect(() => {
    if (currentUser) {
        fetchAllData();
    }
  }, [currentUser, fetchAllData]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigate = useCallback((newView: View) => {
    if (currentUser?.role !== 'admin' && ['servicesManagement', 'documentTemplates'].includes(newView)) {
        alert('Acceso restringido.');
        return;
    }
    const paths: Record<View, string> = {
        landing: '/', dashboard: '/dashboard', newService: '/new-service', calendar: '/calendar',
        cases: '/cases', settings: '/settings', servicesManagement: '/catalog',
        documentTemplates: '/templates', clients: '/clients', caseDetails: '/case-details',
        analytics: '/analytics'
    };
    navigate(paths[newView]);
  }, [currentUser, navigate]);
  
  const handleOpenCase = useCallback((service: any) => {
      setSelectedService(service);
      navigate('/case-details');
  }, [navigate]);
  
  const handleCreateService = useCallback(() => {
      navigate('/new-service');
  }, [navigate]);

  const handleToggleOnboarding = (val: boolean) => {
    setIsOnboardingActive(val);
  };

  // Pantallas condicionales de Auth/Landing
  if (isInitializing) {
    return (
        <div className="h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-slate-900">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
    );
  }

  if (isInitializing) {
    return (
        <div className="h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-slate-900">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
    );
  }

  const isTrialActive = (startDate?: string) => {
    if (!startDate) return false;
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = now.getTime() - start.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  const isSuperUser = currentUser?.email === 'oamengual@gmail.com';
  const hasAccess = !!(currentUser && (currentUser.subscriptionActive || isTrialActive(currentUser.trialStartDate) || isSuperUser));

  const renderContent = () => {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route 
          path="/login" 
          element={
            currentUser && hasAccess 
              ? <Navigate to="/dashboard" replace /> 
              : <AuthScreen onLogin={() => navigate('/dashboard')} onRegister={() => {}} onBack={() => navigate('/')} />
          } 
        />

        <Route 
          path="/*" 
          element={
            !currentUser ? (
              <Navigate to="/login" replace />
            ) : !hasAccess ? (
              <SubscriptionRequiredScreen />
            ) : (
              <Routes>
                <Route path="/dashboard" element={<WelcomeScreen onStart={handleCreateService} onOpenCase={handleOpenCase} />} />
                <Route path="/*" element={
                    <Suspense fallback={
                        <div className="flex h-full w-full items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    }>
                        <Routes>
                            <Route path="/new-service" element={<ServiceCreationScreen onBack={() => handleNavigate('dashboard')} />} />
                            <Route path="/calendar" element={<CalendarScreen />} />
                            <Route path="/cases" element={<CasesScreen onOpenCase={handleOpenCase} />} />
                            <Route path="/case-details" element={<CaseDetailsScreen onBack={() => handleNavigate('cases')} />} />
                            <Route path="/settings" element={<SettingsScreen onNavigate={handleNavigate} isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} isOnboardingActive={isOnboardingActive} onToggleOnboarding={handleToggleOnboarding} onLogout={handleLogout} />} />
                            <Route path="/catalog" element={<ServicesManagementScreen onBack={() => handleNavigate('settings')} />} />
                            <Route path="/templates" element={<DocumentTemplatesScreen onBack={() => handleNavigate('settings')} />} />
                            <Route path="/clients" element={<ClientsScreen onBack={() => handleNavigate('settings')} />} />
                            <Route path="/analytics" element={<AnalyticsScreen />} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </Suspense>
                } />
              </Routes>
            )
          } 
        />
      </Routes>
    );
  };
  
  const shouldShowNav = hasAccess && view !== 'landing' && view !== 'newService' && view !== 'servicesManagement' && view !== 'documentTemplates' && view !== 'clients' && view !== 'caseDetails';

  if (currentUser && currentUser.role === 'admin' && currentUser.onboarding?.completed === false) {
      return (
         <div className="flex h-screen w-full overflow-hidden text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-900 font-sans transition-colors duration-300">
             <OnboardingWizard />
         </div>
      );
  }

  return (
     <div className="flex h-screen overflow-hidden text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-900 font-sans transition-colors duration-300">
        {shouldShowNav && currentUser && (
            <Sidebar 
                activeView={view} 
                onNavigate={handleNavigate} 
                isCollapsed={isSidebarCollapsed} 
                setCollapsed={setSidebarCollapsed} 
                isDarkMode={isDarkMode} 
                userRole={currentUser.role} 
            />
        )}
        <div className="flex flex-col flex-grow h-full overflow-hidden relative">
             <main className="flex-grow overflow-y-auto overflow-x-hidden pt-[max(1.5rem,env(safe-area-inset-top))] pb-32 md:pb-8 scroll-smooth will-change-scroll relative z-0">
                  {renderContent()}
             </main>
             {shouldShowNav && currentUser && (
                <BottomNav 
                    activeView={view} 
                    onNavigate={handleNavigate} 
                    userRole={currentUser.role} 
                />
             )}
             
             {isOnboardingActive && currentUser && (
                <OnboardingGuide 
                    activeView={view} 
                    onClose={() => handleToggleOnboarding(false)} 
                />
             )}
        </div>
    </div>
  );
};

export default App;
